// Top-level navigation model, mirroring the design brief §7.1 web sitemap.
//
// Alerts live INSIDE the Command Center (the CAD right rail, brief §8), not as a separate
// top-level item. "Admin" groups the management surfaces (members/devices/audit/billing/
// branding) behind one entry. Each item may declare a permission key (from Codex's
// account-permissions matrix) required to SEE it; items with none are visible to any
// authenticated console user (data is still RLS-scoped server-side).

import type { NavIconKey } from "./nav-icons";

export interface NavItem {
  key: string;
  label: string;
  path: string;
  /**
   * Stable glyph key for the collapsed icon rail. Required: when the rail is collapsed the
   * icon is the ONLY thing a sighted user sees, so an item without one would be a blank
   * square they cannot identify.
   */
  icon: NavIconKey;
  /** Permission key (public.has_account_permission) required to show this item. */
  permission?: string;
  /** Only visible to platform superadmins. */
  superadminOnly?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { key: "inbox", label: "Inbox", path: "/inbox", icon: "inbox" },
  { key: "command", label: "Command Center", path: "/", icon: "command" },
  { key: "patrols", label: "Patrols", path: "/patrols", icon: "patrols" },
  { key: "schedule", label: "Schedule", path: "/schedule", icon: "schedule" },
  { key: "setup", label: "Setup", path: "/setup", permission: "route.rollout.manage", icon: "setup" },
  { key: "incidents", label: "Incidents", path: "/incidents", icon: "incidents" },
  { key: "reports", label: "Reports", path: "/reports", icon: "reports" },
  { key: "admin", label: "Admin", path: "/admin", permission: "member.manage", icon: "admin" },
  { key: "platform", label: "Platform", path: "/platform", superadminOnly: true, icon: "platform" },
  // Superuser Help shell (design v1) - platform superadmin only, same gate as Platform.
  { key: "help", label: "Help", path: "/help", superadminOnly: true, icon: "help" },
];

export interface NavVisibilityContext {
  can: (permissionKey: string) => boolean;
  isSuperadmin?: boolean;
}

/** Filter the nav to what the current user may see (permission-gated; superadmin-gated). */
export function visibleNavItems(
  items: readonly NavItem[],
  ctx: NavVisibilityContext,
): NavItem[] {
  return items.filter((item) => {
    if (item.superadminOnly === true && ctx.isSuperadmin !== true) {
      return false;
    }
    if (item.permission !== undefined && !ctx.can(item.permission)) {
      return false;
    }
    return true;
  });
}
